import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CourierManagerService } from 'src/app/services/courier-manager/courier-manager.service';
import { CourierManagerModalService } from 'src/app/services/courier-manager-modal/courier-manager-modal.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { CourierCompanyService } from 'src/app/services/courier-company/courier-company.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-add-edit-courier-manager',
  templateUrl: './add-edit-courier-manager.component.html',
  styleUrls: ['./add-edit-courier-manager.component.scss']
})
export class AddEditCourierManagerComponent extends DestructorComponent implements OnInit {

  @Output() courierManagerAdded = new EventEmitter<void>();
  @Output() courierManagerUpdated = new EventEmitter<void>();

  courierManagerForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedCourierManager: any = null;

  // Countries list
  countriesList: any[] = [];

  // Vehicle search properties
  vehicleSearchTerm: string = '';
  vehicleSearchResults: any[] = [];
  selectedVehicle: any = null;
  showVehicleDropdown: boolean = false;
  isVehicleSearching: boolean = false;
  private vehicleSearchSubject = new Subject<string>();

  // Courier Company search properties
  courierCompanySearchTerm: string = '';
  courierCompanySearchResults: any[] = [];
  selectedCourierCompany: any = null;
  showCourierCompanyDropdown: boolean = false;
  isCourierCompanySearching: boolean = false;
  private courierCompanySearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _courierManagerService: CourierManagerService,
    private _courierManagerModalService: CourierManagerModalService,
    private _vehicleService: VehicleService,
    private _courierCompanyService: CourierCompanyService,
    private _globalStateService: GlobalStateService
  ) {
    super();
    this.initializeForm();
    this.setupVehicleSearchDebounce();
    this.setupCourierCompanySearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCountries();
  }

  private initializeForm(): void {
    this.courierManagerForm = this.fb.group({
      vehicle_id: ['', [Validators.required]],
      vehicle_name: ['', [Validators.required]],
      customer_name: [''],
      customer_email: ['', [Validators.email]],
      customer_address: [''],
      post_zip_code: [''],
      country: [''],
      contact_person: [''],
      phone_no: [''],
      courier_company_id: [''],
      courier_company_name: [''],
      order_date: [''],
      surrender_date: [''],
      complete_date: [''],
      docs_released_date: [''],
      tracking_number: [''],
      remarks: [''],
      status: ['Pending']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_courier_manager_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCourierManager = this._courierManagerModalService.getSelectedCourierManager();

        if (serviceCourierManager) {
          this.isEditMode = true;
          this.selectedCourierManager = serviceCourierManager;
          this.populateForm(this.selectedCourierManager);
        } else {
          this.isEditMode = false;
          this.selectedCourierManager = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._courierManagerModalService.clearSelectedCourierManager();
        this.resetForm();
      });
    }
  }

  private loadCountries(): void {
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countriesList = countries || [];
      })
    );
  }

  private populateForm(courierManager: any): void {
    if (courierManager.vehicle) {
      this.selectedVehicle = courierManager.vehicle;
      this.vehicleSearchTerm = this.getVehicleDisplayName(courierManager.vehicle);
    }

    if (courierManager.courier_company) {
      this.selectedCourierCompany = courierManager.courier_company;
      this.courierCompanySearchTerm = this.getCourierCompanyDisplayName(courierManager.courier_company);
    }

    this.courierManagerForm.patchValue({
      vehicle_id: courierManager.vehicle_id || courierManager.vehicle?._id || '',
      vehicle_name: this.vehicleSearchTerm,
      customer_name: courierManager.customer_name || '',
      customer_email: courierManager.customer_email || '',
      customer_address: courierManager.customer_address || '',
      post_zip_code: courierManager.post_zip_code || '',
      country: courierManager.country || '',
      contact_person: courierManager.contact_person || '',
      phone_no: courierManager.phone_no || '',
      courier_company_id: courierManager.courier_company_id || courierManager.courier_company?._id || '',
      courier_company_name: this.courierCompanySearchTerm,
      order_date: courierManager.order_date || '',
      surrender_date: courierManager.surrender_date || '',
      complete_date: courierManager.complete_date || '',
      docs_released_date: courierManager.docs_released_date || '',
      tracking_number: courierManager.tracking_number || '',
      remarks: courierManager.remarks || '',
      status: courierManager.status || 'Pending'
    });
  }

  private resetForm(): void {
    this.courierManagerForm.reset();
    this.courierManagerForm.patchValue({
      status: 'Pending'
    });
    this.isLoading = false;
    this.selectedVehicle = null;
    this.vehicleSearchTerm = '';
    this.vehicleSearchResults = [];
    this.showVehicleDropdown = false;
    this.selectedCourierCompany = null;
    this.courierCompanySearchTerm = '';
    this.courierCompanySearchResults = [];
    this.showCourierCompanyDropdown = false;
  }

  onSubmit(): void {
    if (this.courierManagerForm.valid) {
      this.isLoading = true;
      const formData = { ...this.courierManagerForm.value };

      // Remove display fields
      delete formData.vehicle_name;
      delete formData.courier_company_name;

      if (this.isEditMode && this.selectedCourierManager) {
        this.updateCourierManager(formData);
      } else {
        this.addCourierManager(formData);
      }
    } else {
      this.markFormGroupTouched(this.courierManagerForm);
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }

  private addCourierManager(formData: any): void {
    this.subscriptions.push(
      this._courierManagerService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Courier manager has been added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.courierManagerAdded.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add courier manager.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding courier manager:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding courier manager.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateCourierManager(formData: any): void {
    const courierManagerId = this.selectedCourierManager._id || this.selectedCourierManager.id;
    this.subscriptions.push(
      this._courierManagerService.UpdateData(courierManagerId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Courier manager has been updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.courierManagerUpdated.emit();
              this.resetForm();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update courier manager.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating courier manager:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while updating courier manager.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_courier_manager_add_edit');
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Form control getters
  get vehicle_id() { return this.courierManagerForm.get('vehicle_id'); }
  get vehicle_name() { return this.courierManagerForm.get('vehicle_name'); }
  get customer_name() { return this.courierManagerForm.get('customer_name'); }
  get customer_email() { return this.courierManagerForm.get('customer_email'); }
  get customer_address() { return this.courierManagerForm.get('customer_address'); }
  get post_zip_code() { return this.courierManagerForm.get('post_zip_code'); }
  get country() { return this.courierManagerForm.get('country'); }
  get contact_person() { return this.courierManagerForm.get('contact_person'); }
  get phone_no() { return this.courierManagerForm.get('phone_no'); }
  get courier_company_id() { return this.courierManagerForm.get('courier_company_id'); }
  get courier_company_name() { return this.courierManagerForm.get('courier_company_name'); }
  get order_date() { return this.courierManagerForm.get('order_date'); }
  get surrender_date() { return this.courierManagerForm.get('surrender_date'); }
  get complete_date() { return this.courierManagerForm.get('complete_date'); }
  get docs_released_date() { return this.courierManagerForm.get('docs_released_date'); }
  get tracking_number() { return this.courierManagerForm.get('tracking_number'); }
  get remarks() { return this.courierManagerForm.get('remarks'); }
  get status() { return this.courierManagerForm.get('status'); }

  // Vehicle search methods
  private setupVehicleSearchDebounce(): void {
    this.vehicleSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVehicles(searchTerm);
    });
  }

  onVehicleSearchInput(event: Event): void {
    // Prevent search in edit mode
    if (this.isEditMode) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.vehicleSearchTerm = target.value;
    this.selectedVehicle = null;

    // Update the form control with the typed value
    this.courierManagerForm.patchValue({
      vehicle_name: this.vehicleSearchTerm,
      vehicle_id: ''
    });

    if (this.vehicleSearchTerm.length >= 2) {
      this.vehicleSearchSubject.next(this.vehicleSearchTerm);
      this.showVehicleDropdown = true;
    } else {
      this.showVehicleDropdown = false;
      this.vehicleSearchResults = [];
    }
  }

  onVehicleInputFocus(): void {
    // Prevent dropdown in edit mode
    if (this.isEditMode) {
      return;
    }

    if (this.vehicleSearchResults.length > 0) {
      this.showVehicleDropdown = true;
    }
  }

  onVehicleInputBlur(): void {
    setTimeout(() => {
      this.showVehicleDropdown = false;
    }, 200);
  }

  private searchVehicles(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.vehicleSearchResults = [];
      this.showVehicleDropdown = false;
      return;
    }

    this.isVehicleSearching = true;
    this.subscriptions.push(
      this._vehicleService.GetData('1', searchTerm, { paginate: false, search: searchTerm }).subscribe({
        next: (response: any) => {
          this.isVehicleSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.vehicleSearchResults = response.data.data || response.data || [];
            this.showVehicleDropdown = this.vehicleSearchResults.length > 0;
          } else {
            this.vehicleSearchResults = [];
            this.showVehicleDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVehicleSearching = false;
          console.error('Error searching vehicles:', error);
          this.vehicleSearchResults = [];
          this.showVehicleDropdown = false;
        }
      })
    );
  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);

    this.courierManagerForm.patchValue({
      vehicle_id: vehicle._id || vehicle.id,
      vehicle_name: this.vehicleSearchTerm
    });

    // Populate customer fields if vehicle has customer data and we're in add mode
    if (!this.isEditMode && vehicle.customer) {
      this.populateCustomerFields(vehicle.customer);
    }

    this.showVehicleDropdown = false;
  }

  private getVehicleDisplayName(vehicle: any): string {
    return `${vehicle.car_no || vehicle.inventory_no || 'N/A'} - ${vehicle.name || 'Unknown Vehicle'}`;
  }

  private populateCustomerFields(customer: any): void {
    if (customer) {
      this.courierManagerForm.patchValue({
        customer_name: customer.name || '',
        customer_email: customer.email || '',
        customer_address: customer.address || '',
        post_zip_code: customer.post_zip_code || customer.zip_code || customer.postal_code || '',
        country: customer.country || ''
      });
    }
  }

  // Courier Company search methods
  private setupCourierCompanySearchDebounce(): void {
    this.courierCompanySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchCourierCompanies(searchTerm);
    });
  }

  onCourierCompanySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.courierCompanySearchTerm = target.value;
    this.selectedCourierCompany = null;

    // Update the form control with the typed value
    this.courierManagerForm.patchValue({
      courier_company_name: this.courierCompanySearchTerm,
      courier_company_id: ''
    });

    if (this.courierCompanySearchTerm.length >= 2) {
      this.courierCompanySearchSubject.next(this.courierCompanySearchTerm);
      this.showCourierCompanyDropdown = true;
    } else {
      this.showCourierCompanyDropdown = false;
      this.courierCompanySearchResults = [];
    }
  }

  onCourierCompanyInputFocus(): void {
    if (this.courierCompanySearchResults.length > 0) {
      this.showCourierCompanyDropdown = true;
    }
  }

  onCourierCompanyInputBlur(): void {
    setTimeout(() => {
      this.showCourierCompanyDropdown = false;
    }, 200);
  }

  private searchCourierCompanies(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.courierCompanySearchResults = [];
      this.showCourierCompanyDropdown = false;
      return;
    }

    this.isCourierCompanySearching = true;
    this.subscriptions.push(
      this._courierCompanyService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: any) => {
          this.isCourierCompanySearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.courierCompanySearchResults = response.data.data || response.data || [];
            this.showCourierCompanyDropdown = this.courierCompanySearchResults.length > 0;
          } else {
            this.courierCompanySearchResults = [];
            this.showCourierCompanyDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCourierCompanySearching = false;
          console.error('Error searching courier companies:', error);
          this.courierCompanySearchResults = [];
          this.showCourierCompanyDropdown = false;
        }
      })
    );
  }

  selectCourierCompany(courierCompany: any): void {
    this.selectedCourierCompany = courierCompany;
    this.courierCompanySearchTerm = this.getCourierCompanyDisplayName(courierCompany);

    this.courierManagerForm.patchValue({
      courier_company_id: courierCompany._id || courierCompany.id,
      courier_company_name: this.courierCompanySearchTerm
    });

    this.showCourierCompanyDropdown = false;
  }

  private getCourierCompanyDisplayName(courierCompany: any): string {
    return courierCompany.company_name || courierCompany.name || 'Unknown Company';
  }
}
